import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useGetRequestTypes, useGetSupportRequest, useRequestActions } from 'support/hooks';
import { mapRequestToForm, mapUpsertRequestForm } from 'support/mappers';
import { IRequest, IRequestForm } from 'support/types';
import { validateRequestedDate } from 'support/utils';

import { ApiStatuses, NotificationPosition } from 'coreOld/enums';
import { useNotification } from 'coreOld/hooks/useNotification';

import { requestDetailsSchema } from './requestDetailsSchema';
import RequestDetailsFormView from './RequestDetailsFormView';

interface IProps {
  selectedRequest?: IRequest;
  onClosePanel: () => void;
  isLoading?: boolean;
  isError?: boolean;
}

const RequestDetailsForm = ({ selectedRequest, onClosePanel }: IProps) => {
  const [isEditable, setIsEditable] = useState(false);
  const [showLevelChangeConfirmation, setShowLevelChangeConfirmation] = useState(false);

  const { t } = useTranslation();
  const { clearNotification, showNotification } = useNotification();

  const { records: requestTypes } = useGetRequestTypes();

  const { upsertRequest } = useRequestActions({
    notificationPosition: NotificationPosition.TAB,
  });
  const { record, isLoading, isError } = useGetSupportRequest(
    {
      id: selectedRequest?.id,
    },
    !!selectedRequest
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<IRequestForm>({
    resolver: yupResolver(requestDetailsSchema()),
    defaultValues: mapRequestToForm(record),
  });

  useEffect(() => {
    if (record?.id) {
      reset(mapRequestToForm(record));
    }
  }, [record]);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const errorsMessage = errors && Object.values(errors);

      showNotification({
        message: errorsMessage?.[0]?.message,
        type: ApiStatuses.ERROR,
        position: NotificationPosition.TAB,
      });

      return;
    }

    showNotification(null);
  }, [errors]);

  const onTypeChange = (typeId: number) => {
    if (-1 == typeId) {
      reset(
        mapRequestToForm({
          ...record,
          typeId,
        })
      );

      return;
    }

    const type = requestTypes.find((item) => item.id == typeId);

    if (!type?.levelId) {
      return;
    }

    reset(
      mapRequestToForm({
        ...record,
        typeId: typeId,
        levelId: type?.levelId,
        levelName: type?.levelName,
      })
    );
  };

  const onRequiredDateChange = (date: string) => {
    const showInfo = !validateRequestedDate(date);

    if (showInfo) {
      showNotification({
        message: t('errorRequestedDate2Days'),
        type: ApiStatuses.ERROR,
        position: NotificationPosition.TAB,
      });
    }

    if (!showInfo) {
      clearNotification();
    }
  };

  const onSubmit = async (data: IRequestForm, skipLevelCheck = false) => {
    if (!skipLevelCheck && data.levelId !== record?.levelId) {
      setShowLevelChangeConfirmation(true);

      return;
    }

    setShowLevelChangeConfirmation(false);

    const { success } = await upsertRequest(mapUpsertRequestForm(data));

    if (!success) {
      return;
    }

    onClosePanel();
  };

  const onCancel = () => {
    setIsEditable(false);

    reset(mapRequestToForm(record));
  };

  return (
    <RequestDetailsFormView
      request={record}
      handleSubmit={handleSubmit}
      onSubmit={onSubmit}
      control={control}
      onTypeChange={onTypeChange}
      onRequiredDateChange={onRequiredDateChange}
      isSubmitting={isSubmitting}
      isEditable={isEditable}
      setIsEditable={setIsEditable}
      isLoading={isLoading}
      isError={isError}
      onCancel={onCancel}
      showLevelChangeConfirmation={showLevelChangeConfirmation}
      setShowLevelChangeConfirmation={setShowLevelChangeConfirmation}
      requestTypes={requestTypes}
    />
  );
};

export default RequestDetailsForm;
